import type {
  ExportJob,
  ExportStatus,
  HistoryItem,
  UsageSummary,
} from "@dramapie/shared";

/**
 * M4（版本 / 配额 / 导出）的纯展示逻辑。
 *
 * 这里的数字全部来自服务端，前端只做格式化与阈值判断：
 * 不估算媒体大小、不猜点数、不替服务端报价。
 */

// ---------------------------------------------------------------- 版本历史

type HistorySource = HistoryItem["source"];

export const HISTORY_SOURCE_META: Record<HistorySource, { label: string; code: string }> = {
  ui: { label: "界面", code: "ui" },
  agent: { label: "Agent", code: "agent" },
  tool: { label: "工具", code: "tool" },
  import: { label: "导入", code: "import" },
};

/** 服务端已按 rev 倒序，这里再稳稳排一次：历史面板不依赖响应顺序。 */
export function sortHistoryDesc(items: readonly HistoryItem[]): HistoryItem[] {
  return [...items].sort((a, b) => b.rev - a.rev);
}

export function latestRev(items: readonly HistoryItem[]): number | null {
  if (items.length === 0) return null;
  return items.reduce((max, item) => (item.rev > max ? item.rev : max), items[0]!.rev);
}

/** 撤销是否「可能」有目标：历史上出现过自己的提交（actor 由服务端写，前端不猜）。 */
export function hasOwnCommit(items: readonly HistoryItem[], accountId: string | null): boolean {
  if (!accountId) return false;
  return items.some((item) => item.actor === accountId);
}

/** 回滚说明里的版本跨度：`rev 21 → rev 13`。 */
export function revertLabel(fromRev: number, toRev: number): string {
  return `rev ${fromRev} → rev ${toRev}`;
}

// ---------------------------------------------------------------- 用量 / 配额

/**
 * 百分比（可超过 100）。
 * `limit <= 0` 时：有用量视为 100%（配额缺失不撒谎），没有用量为 0。
 */
export function quotaPercent(used: number, limit: number): number {
  if (!Number.isFinite(used) || used <= 0) return 0;
  if (!Number.isFinite(limit) || limit <= 0) return 100;
  return Math.round((used / limit) * 100);
}

/** 配额条宽度：夹在 0–100，溢出部分用红色文字表达，不画到条外面。 */
export function quotaBarWidth(used: number, limit: number): number {
  return Math.max(0, Math.min(100, quotaPercent(used, limit)));
}

export type QuotaTone = "ok" | "warn" | "over";

/** 用量语气：超限红、≥90% 琥珀、其余中性（视觉规范：只有琥珀一个强调色）。 */
export function quotaTone(used: number, limit: number): QuotaTone {
  if (used > limit) return "over";
  if (quotaPercent(used, limit) >= 90) return "warn";
  return "ok";
}

/** 服务端 `overLimit` 优先；没给就从三个维度自己判断（契约 M4 §2）。 */
export function usageOverLimit(usage: UsageSummary): boolean {
  return (
    usage.overLimit ||
    usage.media.usedBytes > usage.media.limitBytes ||
    usage.projects.used > usage.projects.limit
  );
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const units = ["KB", "MB", "GB", "TB", "PB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}

export function formatPercent(percent: number): string {
  return `${percent.toLocaleString("zh-CN")}%`;
}

// ---------------------------------------------------------------- 导出

export const EXPORT_POLL_INTERVAL_MS = 2_000;
export const EXPORT_POLL_MAX_ATTEMPTS = 60;

/** 签名 URL 的 TTL（契约 M4 §3）：必须显式告诉用户，否则点开时已经过期。 */
export const EXPORT_TTL_NOTE = "下载链接是签名地址，1 小时后过期，请及时保存。";

/** 403 `forbidden`（未开通 `export_zip`）时给用户看的话。 */
export const EXPORT_FORBIDDEN_MESSAGE = "当前套餐不含导出，请升级套餐后再试";

export const EXPORT_STATUS_META: Record<ExportStatus, { label: string }> = {
  queued: { label: "排队中" },
  running: { label: "打包中" },
  succeeded: { label: "已完成" },
  failed: { label: "失败" },
};

export function isTerminalExport(status: ExportStatus): boolean {
  return status === "succeeded" || status === "failed";
}

/** 打印视图是浏览器 `window.open` 的 HTML，不经过 JSON 契约。 */
export function printViewPath(projectId: string, episodeId: string): string {
  const params = new URLSearchParams({ episode: episodeId });
  return `/api/projects/${encodeURIComponent(projectId)}/print?${params.toString()}`;
}

/**
 * 下载链接的剩余有效期文案。
 * 拿不到 `expiresAt` 时退回固定提示，不编造时间。
 */
export function signedUrlExpiryNote(
  expiresAt: number | null,
  now: number = Date.now(),
): string {
  if (expiresAt === null || !Number.isFinite(expiresAt)) return EXPORT_TTL_NOTE;
  const remaining = expiresAt - now;
  if (remaining <= 0) return "下载链接已过期，请重新导出。";
  const minutes = Math.ceil(remaining / 60_000);
  if (minutes <= 60) return `下载链接为签名地址，约 ${minutes} 分钟后过期，请及时保存。`;
  return EXPORT_TTL_NOTE;
}

/** 导出任务的可下载 URL（仅成功且带 url 时）。 */
export function exportDownloadUrl(job: ExportJob): string | null {
  if (job.status !== "succeeded") return null;
  return job.url && job.url.length > 0 ? job.url : null;
}

/**
 * 导出 UI 状态机（页面持有，状态卡渲染）。
 * `forbidden` 单独一档：套餐没开 `export_zip` 时不提供「重试」——
 * 重试还是 403，只会让用户更困惑。
 */
export type ExportUiState =
  | { phase: "idle" }
  | { phase: "creating" }
  | { phase: "polling"; exportId: string; status: ExportStatus; attempts: number }
  | { phase: "done"; job: ExportJob }
  | { phase: "failed"; message: string }
  | { phase: "forbidden"; message: string }
  | { phase: "timeout"; message: string };

// ---------------------------------------------------------------- 键盘

/**
 * 焦点在输入控件里时，`Cmd/Ctrl+Z` 属于「撤销输入」，不能抢。
 * `contenteditable`（Agent 富文本）同理。
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || typeof target !== "object") return false;
  const element = target as HTMLElement;
  if (element.isContentEditable) return true;
  const tag = typeof element.tagName === "string" ? element.tagName.toUpperCase() : "";
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}
